/**
 * Evidence templates for classification system
 * These define the evidence cards that can be generated from scan results
 */

import { EvidenceType, Classification, type EvidenceCard, type ScanResult } from '@/types';

/**
 * Evidence template that can generate evidence cards from scan results
 */
export interface EvidenceTemplate {
  id: string;
  type: EvidenceType;
  description: string;
  supportsClassification: Classification[];
  contradictsClassification: Classification[];
  /** Condition to check if this evidence should be generated */
  condition: (result: ScanResult) => boolean;
  /** Priority for display (lower = more important) */
  priority: number;
}

/**
 * All evidence templates organized by type
 */
export const EVIDENCE_TEMPLATES: EvidenceTemplate[] = [
  // STRUCTURAL EVIDENCE
  {
    id: 'has_cell_membrane',
    type: EvidenceType.STRUCTURAL,
    description: 'Contains cell membrane',
    supportsClassification: [Classification.LIVING],
    contradictsClassification: [Classification.NON_LIVING],
    condition: (result) => result.isLiving && result.cellType !== 'none',
    priority: 1,
  },
  {
    id: 'has_nucleus',
    type: EvidenceType.STRUCTURAL,
    description: 'Contains membrane-bound nucleus',
    supportsClassification: [Classification.LIVING],
    contradictsClassification: [Classification.NON_LIVING],
    condition: (result) => result.cellType === 'eukaryotic' || result.cellType === 'eukaryotic-plant',
    priority: 2,
  },
  {
    id: 'no_nucleus',
    type: EvidenceType.STRUCTURAL,
    description: 'No membrane-bound nucleus (prokaryotic)',
    supportsClassification: [Classification.LIVING, Classification.UNICELLULAR],
    contradictsClassification: [],
    condition: (result) => result.cellType === 'prokaryotic' && result.isLiving,
    priority: 2,
  },
  {
    id: 'single_cell_structure',
    type: EvidenceType.STRUCTURAL,
    description: 'Composed of a single cell',
    supportsClassification: [Classification.UNICELLULAR],
    contradictsClassification: [Classification.MULTICELLULAR],
    condition: (result) => result.cellCount === 'single' || result.cellCount === 1,
    priority: 1,
  },
  {
    id: 'multiple_cells',
    type: EvidenceType.STRUCTURAL,
    description: 'Composed of multiple cells',
    supportsClassification: [Classification.MULTICELLULAR],
    contradictsClassification: [Classification.UNICELLULAR],
    condition: (result) => result.cellCount === 'multiple' || (typeof result.cellCount === 'number' && result.cellCount > 1),
    priority: 1,
  },
  {
    id: 'no_cells',
    type: EvidenceType.STRUCTURAL,
    description: 'No cellular structure detected',
    supportsClassification: [Classification.NON_LIVING],
    contradictsClassification: [Classification.LIVING, Classification.UNICELLULAR, Classification.MULTICELLULAR],
    condition: (result) => result.cellCount === 'none' || result.cellType === 'none',
    priority: 1,
  },
  {
    id: 'has_organelles',
    type: EvidenceType.STRUCTURAL,
    description: 'Contains membrane-bound organelles',
    supportsClassification: [Classification.LIVING],
    contradictsClassification: [Classification.NON_LIVING],
    condition: (result) => result.cellType === 'eukaryotic' || result.cellType === 'eukaryotic-plant',
    priority: 3,
  },
  {
    id: 'has_cell_wall',
    type: EvidenceType.STRUCTURAL,
    description: 'Contains rigid cell wall',
    supportsClassification: [Classification.LIVING],
    contradictsClassification: [],
    condition: (result) => result.cellType === 'eukaryotic-plant' || result.cellType === 'prokaryotic',
    priority: 4,
  },
  {
    id: 'has_chloroplasts',
    type: EvidenceType.STRUCTURAL,
    description: 'Contains chloroplasts for photosynthesis',
    supportsClassification: [Classification.LIVING],
    contradictsClassification: [Classification.NON_LIVING],
    condition: (result) => result.cellType === 'eukaryotic-plant',
    priority: 3,
  },

  // FUNCTIONAL EVIDENCE
  {
    id: 'performs_metabolism',
    type: EvidenceType.FUNCTIONAL,
    description: 'Performs metabolic processes',
    supportsClassification: [Classification.LIVING],
    contradictsClassification: [Classification.NON_LIVING],
    condition: (result) => result.detectedFunctions.some(f => f === 'METABOLISM'),
    priority: 1,
  },
  {
    id: 'can_reproduce',
    type: EvidenceType.FUNCTIONAL,
    description: 'Capable of reproduction',
    supportsClassification: [Classification.LIVING],
    contradictsClassification: [Classification.NON_LIVING],
    condition: (result) => result.detectedFunctions.some(f => f === 'REPRODUCTION'),
    priority: 2,
  },
  {
    id: 'responds_to_stimuli',
    type: EvidenceType.FUNCTIONAL,
    description: 'Responds to environmental stimuli',
    supportsClassification: [Classification.LIVING],
    contradictsClassification: [Classification.NON_LIVING],
    condition: (result) => result.detectedFunctions.some(f => f === 'RESPONSE_TO_STIMULI'),
    priority: 2,
  },
  {
    id: 'maintains_homeostasis',
    type: EvidenceType.FUNCTIONAL,
    description: 'Maintains internal balance (homeostasis)',
    supportsClassification: [Classification.LIVING],
    contradictsClassification: [Classification.NON_LIVING],
    condition: (result) => result.detectedFunctions.some(f => f === 'HOMEOSTASIS'),
    priority: 3,
  },
  {
    id: 'shows_growth',
    type: EvidenceType.FUNCTIONAL,
    description: 'Shows evidence of growth',
    supportsClassification: [Classification.LIVING],
    contradictsClassification: [Classification.NON_LIVING],
    condition: (result) => result.detectedFunctions.some(f => f === 'GROWTH'),
    priority: 3,
  },
  {
    id: 'no_life_functions',
    type: EvidenceType.FUNCTIONAL,
    description: 'No life functions detected',
    supportsClassification: [Classification.NON_LIVING],
    contradictsClassification: [Classification.LIVING],
    condition: (result) => result.detectedFunctions.length === 0,
    priority: 1,
  },

  // COMPOSITIONAL EVIDENCE
  {
    id: 'organic_molecules',
    type: EvidenceType.COMPOSITIONAL,
    description: 'Contains organic molecules (proteins, DNA)',
    supportsClassification: [Classification.LIVING],
    contradictsClassification: [],
    condition: (result) => result.isLiving,
    priority: 4,
  },
  {
    id: 'inorganic_composition',
    type: EvidenceType.COMPOSITIONAL,
    description: 'Composed of inorganic materials',
    supportsClassification: [Classification.NON_LIVING],
    contradictsClassification: [Classification.LIVING],
    condition: (result) => !result.isLiving && result.cellType === 'none',
    priority: 4,
  },

  // BEHAVIORAL EVIDENCE
  {
    id: 'autonomous_movement',
    type: EvidenceType.BEHAVIORAL,
    description: 'Exhibits autonomous movement',
    supportsClassification: [Classification.LIVING],
    contradictsClassification: [Classification.NON_LIVING],
    condition: (result) => result.detectedFunctions.some(f => f === 'RESPONSE_TO_STIMULI'),
    priority: 5,
  },
  {
    id: 'no_autonomous_activity',
    type: EvidenceType.BEHAVIORAL,
    description: 'No autonomous activity observed',
    supportsClassification: [Classification.NON_LIVING],
    contradictsClassification: [],
    condition: (result) => !result.isLiving && result.detectedFunctions.length === 0,
    priority: 5,
  },

  // CONTEXTUAL EVIDENCE
  {
    id: 'biological_origin',
    type: EvidenceType.CONTEXTUAL,
    description: 'Found in biological sample',
    supportsClassification: [Classification.LIVING],
    contradictsClassification: [],
    condition: (result) => result.isLiving,
    priority: 6,
  },
  {
    id: 'environmental_debris',
    type: EvidenceType.CONTEXTUAL,
    description: 'Appears to be environmental debris',
    supportsClassification: [Classification.NON_LIVING],
    contradictsClassification: [Classification.LIVING],
    condition: (result) => !result.isLiving && result.cellType === 'none',
    priority: 6,
  },
];

/**
 * Generate evidence cards from a scan result
 * @param result The scan result to generate evidence from
 * @returns Array of evidence cards that match the scan result
 */
export function generateEvidenceFromScan(result: ScanResult): EvidenceCard[] {
  const matchingTemplates = EVIDENCE_TEMPLATES
    .filter(template => template.condition(result))
    .sort((a, b) => a.priority - b.priority);

  return matchingTemplates.map(template => ({
    id: template.id,
    type: template.type,
    description: template.description,
    supportsClassification: [...template.supportsClassification],
    contradictsClassification: [...template.contradictsClassification],
  }));
}

/**
 * Get evidence templates by type
 */
export function getTemplatesByType(type: EvidenceType): EvidenceTemplate[] {
  return EVIDENCE_TEMPLATES.filter(t => t.type === type);
}

/**
 * Get evidence icon for a type
 */
export function getEvidenceIcon(type: EvidenceType): string {
  switch (type) {
    case EvidenceType.STRUCTURAL:
      return '🔬'; // Microscope for structure
    case EvidenceType.FUNCTIONAL:
      return '⚡'; // Lightning for function/activity
    case EvidenceType.COMPOSITIONAL:
      return '🧪'; // Test tube for composition
    case EvidenceType.BEHAVIORAL:
      return '🎯'; // Target for behavior
    case EvidenceType.CONTEXTUAL:
      return '📍'; // Pin for context
    default:
      return '📋';
  }
}

/**
 * Get color for evidence type (hex values for Phaser)
 */
export function getEvidenceColor(type: EvidenceType): number {
  switch (type) {
    case EvidenceType.STRUCTURAL:
      return 0x4299e1; // Blue
    case EvidenceType.FUNCTIONAL:
      return 0xed8936; // Orange
    case EvidenceType.COMPOSITIONAL:
      return 0x48bb78; // Green
    case EvidenceType.BEHAVIORAL:
      return 0x9f7aea; // Purple
    case EvidenceType.CONTEXTUAL:
      return 0x718096; // Gray
    default:
      return 0x718096;
  }
}

/**
 * Classification feedback messages
 */
export const CLASSIFICATION_FEEDBACK = {
  correct: {
    living: 'Correct! This specimen shows clear signs of life.',
    nonLiving: 'Correct! This is not a living organism.',
    unicellular: 'Correct! This is a single-celled organism.',
    multicellular: 'Correct! This organism is made of multiple cells.',
  },
  incorrect: {
    livingAsNonLiving: 'Not quite. Look for cellular structures and life functions.',
    nonLivingAsLiving: 'Not quite. Check for actual cellular organization.',
    unicellularAsMulticellular: 'Look more carefully - how many cells do you see?',
    multicellularAsUnicellular: 'Count the cells again - there may be more than one.',
    needMoreEvidence: 'Collect more evidence before classifying.',
  },
  hints: {
    living: 'Living things have cells, perform metabolism, and can reproduce.',
    nonLiving: 'Non-living things lack cells and life functions.',
    unicellular: 'Unicellular organisms have only one cell that does everything.',
    multicellular: 'Multicellular organisms have many specialized cells.',
  },
};
